import { createContext, useContext, useState, useCallback } from "react";

/**
 * TopNavAction — a simple portal-style slot that pages can fill with a
 * contextual action button (e.g., "Save Settings" on the Global Settings
 * page). `AdminLayout` renders `action` inside the top nav; consumer
 * pages call `setAction(node)` in an effect and clear on unmount.
 */
const TopNavActionContext = createContext(null);

export function TopNavActionProvider({ children }) {
  const [action, setAction] = useState(null);

  const clear = useCallback(() => setAction(null), []);
  const set = useCallback((node) => setAction(node), []);

  return (
    <TopNavActionContext.Provider value={{ action, setAction: set, clearAction: clear }}>
      {children}
    </TopNavActionContext.Provider>
  );
}

export function useTopNavAction() {
  const ctx = useContext(TopNavActionContext);
  if (!ctx) {
    // Render-time pages that don't have the provider mounted (e.g.
    // learner portal) silently no-op instead of throwing — lets the
    // same hook be imported safely from shared components.
    return { action: null, setAction: () => {}, clearAction: () => {} };
  }
  return ctx;
}
